import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { OrderService } from './order.service';
import { Order } from '../models/order';
import { OrderInfo } from '../models/order-info';
import { LoggerService } from '../../../services/logger/logger.service';
import { ConnectionService } from '../../../services/connection/connection.service';
import { TimeRestriction } from '../../booking/models/time-restriction';
import { ShopSettingsService } from '../../general/services/shop-settings.service';


@Injectable()
export class OrderOdooService implements OrderService {

  constructor(
    private logger: LoggerService,
    private connectionService: ConnectionService,
    private shopSettingsService: ShopSettingsService,
  ) {
  }

  createOrder(order: Order): Observable<Order> {
    this.logger.info(`OrderOdooService: createOrder requested.`);
    const isKioskMode = this.shopSettingsService.isVUMode;
    return this.connectionService.sendJsonPostRequest('/store/order/create-order', {
      order: order.toJSON(),
      isKioskMode,
    }).pipe(map(
      createdOrder => {
        this.logger.info(`OrderOdooService: createOrder received, order id: ${createdOrder.id}.`);
        return Order.createFromAny(createdOrder);
      }
    ));
  }

  createGuestOrder(order: Order): Observable<Order> {
    this.logger.info(`OrderOdooService: createGuestOrder requested.`);
    return this.connectionService.sendJsonPostRequest('/store/order/create-guest-order', {
      order: order.toJSON(),
    }).pipe(map(
      createdOrder => {
        this.logger.info(`OrderOdooService: createGuestOrder received, order id: ${createdOrder.id}.`);
        return Order.createFromAny(createdOrder);
      }
    ));
  }

  saleToInvoice(orderId: number): Observable<boolean> {
    this.logger.info(`OrderOdooService: saleToInvoice requested.`);
    return this.connectionService.sendJsonPostRequest('/store/order/sale-to-invoice', { orderId }).pipe(map(
      result => {
        this.logger.info(`OrderOdooService: saleToInvoice received, order id: ${orderId}, result: ${orderId}.`);
        return result;
      }
    ));
  }

  getOrder(orderId: number): Observable<Order> {
    this.logger.info(`OrderOdooService: getOrder requested.`);
    return this.connectionService.sendJsonPostRequest('/store/order/get-order', { orderId }).pipe(map(
      order => {
        this.logger.info(`OrderOdooService: getOrder received, order id: ${order.id}.`);
        return Order.createFromAny(order);
      }
    ));
  }

  getGuestOrder(orderId: number, orderKey: string): Observable<Order> {
    this.logger.info(`OrderOdooService: getGuestOrder requested.`);
    return this.connectionService.sendJsonPostRequest('/store/order/get-guest-order', { orderId, orderKey }).pipe(map(
      order => {
        this.logger.info(`OrderOdooService: getGuestOrder received, order id: ${order.id}.`);
        return Order.createFromAny(order);
      }
    ));
  }

  get loggedInPartnerOrders(): Observable<OrderInfo[]> {
    this.logger.info(`OrderOdooService: loggedInPartnerOrders requested.`);
    return this.connectionService.sendJsonPostRequest('/store/order/my-orders').pipe(map(
      orders => {
        if (!orders) {
          return null;
        }

        this.logger.info(`OrderOdooService: loggedInPartnerOrders received`);
        return orders.map(x => OrderInfo.createFromAny(x));
      }
    ));
  }

  changeOrderTimeSlot(orderId: number, date: Date, timeSlotId: number): Observable<any> {
    this.logger.info(`OrderOdooService: changeOrderTimeSlot requested.`);
    return this.connectionService.sendJsonPostRequest('/store/order/change', { orderId, date, timeSlotId }).pipe(map(
      result => {
        this.logger.info(`OrderOdooService: changeOrderTimeSlot received, order id: ${orderId}, result: ${orderId}.`);
        return result;
      }
    ));
  }

  getOrderRestrictions(orderId: number): Observable<TimeRestriction[]> {
    return this.connectionService.sendJsonPostRequest('/store/order/time-restrictions', {
      orderId,
    }).pipe(map(
      result => {
        if (!result) {
          return null;
        }

        return result.map(x => TimeRestriction.createFromAny(x));
      }
    ));
  }

  cancelOrder(orderId: number): Observable<any> {
    this.logger.info(`OrderOdooService: changeOrderTimeSlot requested.`);
    return this.connectionService.sendJsonPostRequest('/store/order/cancel', { orderId }).pipe(tap(
      result => {
        this.logger.info(`OrderOdooService: cancelOrder received, order id: ${orderId}, result: ${result ? result.message : ''}.`);
        if (!result || !result.result) {
          let errorMessage = 'Cancel order failed';
          if (result && result.message) {
            errorMessage = result.message;
          }
          throw Error(errorMessage);
        }
      }
    ));
  }

  refundOrder(orderId: number): Observable<any> {
    this.logger.info(`OrderOdooService: refundOrder requested.`);
    return this.connectionService.sendJsonPostRequest('/store/order/refund-order', { orderId }).pipe(tap(
      result => {
        this.logger.info(`OrderOdooService: refundOrder received, order id: ${orderId}, result: ${result ? result.message : ''}.`);
        if (!result || !result.result) {
          let errorMessage = 'Refund order failed';
          if (result && result.message) {
            errorMessage = result.message;
          }
          throw Error(errorMessage);
        }
      }
    ));
  }

  sendGuestOrderToEmail(orderId: number, orderKey: string, email: string): Observable<boolean> {
    this.logger.info(`OrderOdooService: sendGuestOrderToEmail requested.`);
    return this.connectionService.sendJsonPostRequest('/email/send-guest-order', { orderId, orderKey, email }).pipe(map(
      result => {
        this.logger.info(`OrderOdooService: sendGuestOrderToEmail received, order id: ${orderId}.`);
        return result;
      }
    ));
  }

}
